import React, { type ReactNode, useEffect } from "react"
import { ChevronDown, ChevronUp, Rss, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import { Checkbox } from "@/shadcn/components/ui/checkbox"
import { Input } from "@/shadcn/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shadcn/components/ui/pagination"
import { useRecordEvent } from "@/lib/use-record-event"

// DEV HANDOFF ─ "Add social to feeds" dialog. Figma: S311lGXE6JSyTnNAXHmtR7,
// frames 28364-652 (browse), 28485-893 (confirm collapsed), 28485-3769 (confirm
// expanded). One component, two `step`s ("browse" | "confirm"), driven entirely
// by props with defaults from the design so it drops into prod unwired.
//
// Renders the DIALOG SURFACE only (not a portaling <Dialog>) so it stays
// self-contained and pixel-reviewable; in prod, drop this inside your Dialog and
// wire the surrounding data/handlers.
//
// Every control takes an optional handler and falls back to the uncontrolled
// design defaults when it isn't given, so the file still renders standalone for
// pixel review. NewSocialSuggestFeedsModal is the production wiring: it passes
// `selectedIds` + `onToggleFeed`/`onToggleFeeds` (making the checkboxes
// controlled), `search`, pagination, and the step handlers. Sort & filter is the
// one control with no behaviour behind it yet — its button only renders when an
// `onSortFilter` handler is supplied, so no dead control ships.
//
// Feed logos come from the backend: pass each feed a `logo` node and it fills the
// tile; when omitted, the tile defaults to the RSS glyph (no-image state).
//
// FONT NOTE: the Figma header title + description and the pagination labels are
// set in Geist; this renders them in the theme font (Inter) per project rules —
// flag with the user if Geist is required.

export type SuggestFeed = {
  id: string
  name: string
  /** Right-aligned relative time on line 1, e.g. "2h ago". */
  updatedAgo?: string
  /** Muted line-2 label, e.g. "~308 updates / week". */
  cadence?: string
  /**
   * Muted line-2 label supplied by the wiring (the route the feed belongs to).
   * Wins over `cadence`, which stays reserved for a backend update rate.
   */
  subtitle?: string
  /** Small muted line-2 trailing note, e.g. "most recent". */
  note?: string
  /** Initial checkbox state (uncontrolled). */
  selected?: boolean
  /** A real logo node (prod, sent by the backend) — defaults to the RSS glyph tile when omitted. */
  logo?: ReactNode
}

type NewSocialSuggestFeedsProps = {
  className?: string
  /** "browse" = full picker modal, "confirm" = compact confirmation dialog. */
  step?: "browse" | "confirm"
  /** Confirm step only: whether the selected-feeds list is expanded. */
  feedsExpanded?: boolean
  /**
   * Confirm step only — which spacing spec to render. Review aid for comparing
   * the Figma frame ("figma", default) against the shadcn AlertDialog
   * primitive's spacing ("shadcn"). Prod uses "figma".
   */
  spacing?: "figma" | "shadcn"
  /** Social account being added, e.g. "@dlvrit_news". */
  handle?: string
  title?: string
  description?: string
  /** Initials for the account avatar in the browse header. */
  avatarFallback?: string
  /** A real avatar node for the browse header — falls back to the initials tile. */
  avatar?: ReactNode
  /** Shown in place of the feed rows when the list is empty. */
  emptyLabel?: string
  /** Total feed count shown in the search placeholder. */
  totalFeeds?: number
  recommendedLabel?: string
  recommended?: SuggestFeed[]
  allFeedsLabel?: string
  /** Count shown next to the "ALL FEEDS" label (total across pages). */
  allFeedsCount?: number
  allFeeds?: SuggestFeed[]
  /** Current pagination page. */
  page?: number
  /** Numbered pages to render (before the ellipsis). */
  pages?: number[]
  /** Total page count — drives the ellipsis and the prev/next disabled states. */
  totalPages?: number
  /** Confirm step: initial state of the "Share most recent item" checkbox. */
  shareMostRecent?: boolean
  /**
   * Controlled selection, by feed id. When omitted each row falls back to its
   * own uncontrolled `selected` flag.
   */
  selectedIds?: string[]
  /**
   * Confirm step: the feeds to count and list. Defaults to the feeds flagged
   * `selected`; the wiring passes the real selection, which spans pages.
   */
  selectedFeeds?: SuggestFeed[]
  /** Disables the primary actions while a save is in flight. */
  busy?: boolean
  /** Confirm step: why the last save failed, shown above the actions. */
  error?: string
  /** Controlled search term. */
  search?: string
  onSearchChange?: (value: string) => void
  onToggleFeed?: (id: string, checked: boolean) => void
  /** Section "select all" — the ids it covers, and the state to set them to. */
  onToggleFeeds?: (ids: string[], checked: boolean) => void
  onPageChange?: (page: number) => void
  /** Renders the "Sort & filter" button; the button is hidden without it. */
  onSortFilter?: () => void
  onClose?: () => void
  onSkip?: () => void
  onContinue?: () => void
  onCancel?: () => void
  onConfirm?: () => void
  onToggleFeedsExpanded?: () => void
  onShareMostRecentChange?: (checked: boolean) => void
}

const RECOMMENDED_FEEDS: SuggestFeed[] = [
  {
    id: "buzzfeed",
    name: "BuzzFeed",
    updatedAgo: "2h ago",
    cadence: "~308 updates / week",
    note: "most recent",
    selected: true,
  },
  {
    id: "nyt-connections",
    name: "NYT Connections",
    updatedAgo: "1h ago",
    cadence: "~42 updates / week",
    note: "most recent",
    selected: true,
  },
  {
    id: "techcrunch-digest",
    name: "Tech Crunch Digest",
    updatedAgo: "30m ago",
    cadence: "~85 updates / week",
    note: "most recent",
    selected: true,
  },
]

const ALL_FEEDS: SuggestFeed[] = [
  {
    id: "garbanzoboy-posts",
    name: "garbanzoboy Posts",
    updatedAgo: "Yesterday",
    cadence: "~4 updates / week",
    note: "most recent",
  },
  {
    id: "podcast-weekly-roundup",
    name: "Podcast Weekly Roundup",
    updatedAgo: "3d ago",
    cadence: "~2 updates / week",
    note: "most recent",
  },
  {
    id: "old-project-updates",
    name: "Old Project Updates",
    updatedAgo: "3w ago",
    cadence: "~0 updates / week",
    note: "most recent",
  },
  {
    id: "hacker-news",
    name: "Hacker News",
    updatedAgo: "5h ago",
    cadence: "~12 updates / week",
    note: "most recent",
  },
]

const feedCount = (count: number) => `${count} ${count === 1 ? "feed" : "feeds"}`

function CloseButton({ onClose }: { onClose?: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className="tw:absolute tw:top-4 tw:right-4 tw:rounded-xs tw:text-muted-foreground tw:opacity-70 tw:transition-opacity tw:outline-none tw:hover:opacity-100 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
    >
      <X className="tw:size-4" />
    </button>
  )
}

function FeedLogo({
  feed,
  size = "md",
}: {
  feed: SuggestFeed
  size?: "md" | "sm"
}) {
  const tile =
    size === "md" ? "tw:size-9 tw:rounded-md" : "tw:size-6 tw:rounded-sm"
  const icon = size === "md" ? "tw:size-5" : "tw:size-3.5"

  if (feed.logo) {
    return (
      <div className={cn("tw:shrink-0 tw:overflow-hidden", tile)}>
        {feed.logo}
      </div>
    )
  }
  // No logo from the backend → default RSS glyph tile.
  return (
    <div
      className={cn(
        "tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:bg-muted tw:text-muted-foreground",
        tile
      )}
    >
      <Rss className={icon} />
    </div>
  )
}

function FeedRow({
  feed,
  checked,
  onToggle,
}: {
  feed: SuggestFeed
  /** Undefined leaves the row uncontrolled, on the feed's own `selected` flag. */
  checked?: boolean
  onToggle?: (checked: boolean) => void
}) {
  const selection =
    checked === undefined
      ? { defaultChecked: feed.selected }
      : { checked, onCheckedChange: (next: boolean | "indeterminate") => onToggle?.(next === true) }

  return (
    <div className="tw:flex tw:items-start tw:gap-3 tw:rounded-md tw:px-3 tw:py-2.5">
      <Checkbox {...selection} aria-label={`Select ${feed.name}`} />
      <FeedLogo feed={feed} />
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <span className="tw:truncate tw:text-sm tw:font-medium tw:text-foreground">
            {feed.name}
          </span>
          {feed.updatedAgo ? (
            <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
              {feed.updatedAgo}
            </span>
          ) : null}
        </div>
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          {(feed.subtitle ?? feed.cadence) ? (
            <span className="tw:min-w-0 tw:truncate tw:text-xs tw:text-muted-foreground">
              {feed.subtitle ?? feed.cadence}
            </span>
          ) : null}
          {feed.note ? (
            <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground/60">
              {feed.note}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function NewSocialSuggestFeeds({
  className,
  step = "browse",
  feedsExpanded = false,
  spacing = "figma",
  handle = "@dlvrit_news",
  title = "Add @dlvrit_news to your feeds",
  description = "Pick which feeds should also post to this X account.",
  avatarFallback = "DN",
  avatar,
  emptyLabel = "No feeds match that search.",
  totalFeeds = 52,
  recommendedLabel = "MOST ACTIVE",
  recommended = RECOMMENDED_FEEDS,
  allFeedsLabel = "ALL FEEDS",
  allFeedsCount = 48,
  allFeeds = ALL_FEEDS,
  page = 1,
  pages = [1, 2, 3, 4],
  totalPages,
  shareMostRecent = true,
  selectedIds,
  selectedFeeds,
  busy = false,
  error = "",
  search,
  onSearchChange,
  onToggleFeed,
  onToggleFeeds,
  onPageChange,
  onSortFilter,
  onClose,
  onSkip,
  onContinue,
  onCancel,
  onConfirm,
  onToggleFeedsExpanded,
  onShareMostRecentChange,
}: NewSocialSuggestFeedsProps) {
  const controlled = selectedIds !== undefined
  const selection = new Set(selectedIds ?? [])
  const isSelected = (feed: SuggestFeed) =>
    controlled ? selection.has(feed.id) : undefined
  const recordEvent = useRecordEvent();

  useEffect(() => {
    recordEvent("New Social Suggest Feeds Modal Opened");
  }, []);

  const chosen =
    selectedFeeds ??
    (controlled
      ? [...recommended, ...allFeeds].filter((f) => selection.has(f.id))
      : [...recommended, ...allFeeds].filter((f) => f.selected))
  const selectedCount = chosen.length

  // A section header is checked only when every row under it is, and shows the
  // dash state as soon as the selection is partial.
  const sectionState = (feeds: SuggestFeed[]): boolean | "indeterminate" | undefined => {
    if (!controlled) {
      return undefined
    }
    const picked = feeds.filter((feed) => selection.has(feed.id)).length
    if (!picked) {
      return false
    }
    return picked === feeds.length ? true : "indeterminate"
  }

  const sectionToggle = (feeds: SuggestFeed[]) =>
    controlled
      ? {
          checked: sectionState(feeds),
          onCheckedChange: (next: boolean | "indeterminate") =>
            onToggleFeeds?.(feeds.map((feed) => feed.id), next === true),
        }
      : {}

  /*
   * "Select all on this page" is the page checkbox's twin, so it is a real
   * control whenever there is a handler behind it; unwired (design preview) it
   * stays the frame's plain label rather than a button that does nothing. The
   * count comes from the page, not the section total next to "ALL FEEDS", so the
   * action says exactly what it will select.
   */
  const pageSelectAll = onToggleFeeds ? (
    <button
      type="button"
      className="tw:text-xs tw:text-muted-foreground/70 tw:transition-colors tw:hover:text-foreground tw:disabled:opacity-50"
      disabled={busy || !allFeeds.length}
      onClick={() =>
        onToggleFeeds(
          allFeeds.map((feed) => feed.id),
          sectionState(allFeeds) !== true
        )
      }
    >
      {sectionState(allFeeds) === true
        ? "Clear this page"
        : `Select all ${allFeeds.length} on this page`}
    </button>
  ) : (
    <span className="tw:text-xs tw:text-muted-foreground/70">
      Select all on this page
    </span>
  )

  const goToPage = (event: React.MouseEvent, next: number) => {
    event.preventDefault()
    if (onPageChange && next >= 1 && (!totalPages || next <= totalPages)) {
      onPageChange(next)
    }
  }

  if (step === "confirm") {
    // `spacing` lets the review harness compare the Figma frame's spacing
    // against the shadcn AlertDialog primitive's spacing. Prod uses "figma".
    const isShadcnSpacing = spacing === "shadcn"
    const heading = `Add ${handle} to ${feedCount(selectedCount)}`
    const subtext = `New items will post to your social.`
    return (
      <div
        className={cn(
          "tw:relative tw:mx-auto tw:flex tw:w-full tw:max-w-md tw:flex-col tw:items-center tw:rounded-xl tw:border tw:border-border tw:bg-card tw:text-card-foreground",
          isShadcnSpacing ? "tw:gap-6 tw:p-6" : "tw:gap-4 tw:p-8",
          className
        )}
      >
        <CloseButton onClose={onClose} />

        <div className="tw:flex tw:size-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-secondary">
          <Rss className="tw:size-7 tw:text-primary" />
        </div>

        {isShadcnSpacing ? (
          // shadcn AlertDialogHeader: title + description grouped at gap-1.5
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5">
            <h2 className="tw:text-center tw:font-heading tw:text-lg tw:font-medium tw:text-foreground">
              {heading}
            </h2>
            <p className="tw:text-center tw:text-sm tw:text-muted-foreground">
              {subtext}
            </p>
          </div>
        ) : (
          <>
            <h2 className="tw:text-center tw:font-heading tw:text-xl tw:font-semibold tw:text-foreground">
              {heading}
            </h2>
            <p className="tw:text-center tw:text-sm tw:text-muted-foreground">
              {subtext}
            </p>
          </>
        )}

        <button
          type="button"
          className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-2.5 tw:py-1.5 tw:text-sm tw:font-medium tw:text-muted-foreground tw:transition-colors tw:outline-none tw:hover:bg-accent tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
          aria-expanded={feedsExpanded}
          onClick={onToggleFeedsExpanded}
        >
          {feedsExpanded ? "Hide feeds" : `Show ${feedCount(selectedCount)}`}
          {feedsExpanded ? (
            <ChevronUp className="tw:size-3.5" />
          ) : (
            <ChevronDown className="tw:size-3.5" />
          )}
        </button>

        {feedsExpanded ? (
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-1.5">
            {chosen.map((feed) => (
              <div
                key={feed.id}
                className="tw:flex tw:items-center tw:gap-2.5 tw:rounded-md tw:bg-muted tw:px-2.5 tw:py-2"
              >
                <FeedLogo feed={feed} size="sm" />
                <span className="tw:truncate tw:text-sm tw:font-medium tw:text-foreground">
                  {feed.name}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <label className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2">
          <Checkbox
            {...(onShareMostRecentChange
              ? {
                  checked: shareMostRecent,
                  onCheckedChange: (next: boolean | "indeterminate") =>
                    onShareMostRecentChange(next === true),
                }
              : { defaultChecked: shareMostRecent })}
          />
          <span className="tw:text-sm tw:font-medium tw:text-foreground">
            Share most recent item
          </span>
        </label>

        {isShadcnSpacing ? null : <div className="tw:h-2" />}

        {error ? (
          <p
            role="alert"
            className="tw:w-full tw:text-center tw:text-sm tw:text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
          <Button
            size="sm"
            className="tw:w-full"
            disabled={busy || !selectedCount}
            onClick={onConfirm}
          >
            {error ? "Try again" : `Add to ${feedCount(selectedCount)}`}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="tw:w-full"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "tw:relative tw:mx-auto tw:flex tw:w-full tw:max-w-md tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card tw:text-card-foreground",
        className
      )}
    >
      <CloseButton onClose={onClose} />

      {/* Header */}
      <div className="tw:flex tw:items-center tw:gap-3 tw:px-5 tw:pt-7 tw:pb-5">
        <div className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:bg-muted tw:text-sm tw:font-medium tw:text-muted-foreground">
          {avatar ?? avatarFallback}
        </div>
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
          <p className="tw:truncate tw:font-heading tw:text-base tw:font-medium tw:text-foreground">
            {title}
          </p>
          <p className="tw:text-sm tw:text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Dialog content — Figma "Dialog Content": gap-8 between sections, pb-16 */}
      <div className="tw:flex tw:flex-col tw:gap-2 tw:pb-4">
        {/* Search row */}
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-5 tw:pb-3.5">
          <Input
            className="tw:flex-1"
            placeholder={`Search ${feedCount(totalFeeds)}`}
            {...(onSearchChange
              ? {
                  value: search ?? "",
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    onSearchChange(event.target.value),
                }
              : {})}
          />
          {onSortFilter ? (
            <Button
              variant="outline"
              size="sm"
              className="tw:shrink-0"
              onClick={onSortFilter}
            >
              Sort &amp; filter
            </Button>
          ) : null}
        </div>

        {/* Recommended section */}
        {recommended.length ? (
          <>
            <div className="tw:flex tw:items-center tw:gap-2 tw:border-y tw:border-border tw:bg-muted tw:px-5 tw:py-2.5">
              <Checkbox
                {...(controlled ? {} : { defaultChecked: true })}
                {...sectionToggle(recommended)}
                aria-label={`Select all ${recommendedLabel}`}
              />
              <span className="tw:text-xs tw:font-medium tw:tracking-wider tw:text-muted-foreground tw:uppercase">
                {recommendedLabel} · {recommended.length}
              </span>
            </div>
            <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-2 tw:py-1">
              {recommended.map((feed) => (
                <FeedRow
                  key={feed.id}
                  feed={feed}
                  checked={isSelected(feed)}
                  onToggle={(next) => onToggleFeed?.(feed.id, next)}
                />
              ))}
            </div>
          </>
        ) : null}

        {/* All feeds section */}
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-y tw:border-border tw:bg-muted tw:px-5 tw:py-2.5">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Checkbox
              {...sectionToggle(allFeeds)}
              aria-label="Select all feeds on this page"
            />
            <span className="tw:text-xs tw:font-medium tw:tracking-wider tw:text-muted-foreground tw:uppercase">
              {allFeedsLabel} · {allFeedsCount}
            </span>
          </div>
          {pageSelectAll}
        </div>
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-2 tw:py-1">
          {allFeeds.length ? (
            allFeeds.map((feed) => (
              <FeedRow
                key={feed.id}
                feed={feed}
                checked={isSelected(feed)}
                onToggle={(next) => onToggleFeed?.(feed.id, next)}
              />
            ))
          ) : (
            <p className="tw:px-3 tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground">
              {emptyLabel}
            </p>
          )}
        </div>

        {/* Pagination */}
        {pages.length > 1 ? (
          <Pagination className="tw:py-2.5">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page <= 1}
                  onClick={(event) => goToPage(event, page - 1)}
                />
              </PaginationItem>
              {pages.map((p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    href="#"
                    isActive={p === page}
                    onClick={(event) => goToPage(event, p)}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {!totalPages || pages[pages.length - 1] < totalPages ? (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : null}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={Boolean(totalPages) && page >= totalPages!}
                  onClick={(event) => goToPage(event, page + 1)}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        ) : null}
      </div>

      {/* Footer */}
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-muted/40 tw:p-4">
        <Button variant="outline" size="sm" disabled={busy} onClick={onSkip}>
          Skip
        </Button>
        <Button
          size="sm"
          disabled={busy || (controlled && !selectedCount)}
          onClick={onContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
